/* eslint-disable @typescript-eslint/array-type */
import { createGuid, Guid } from 'app/types/guid';
import { List, Record } from 'immutable';

declare const EntityIdBrand: unique symbol;

export type EntityId = { readonly [EntityIdBrand]: unique symbol };

export type EntityStringId = EntityId & Guid;
export type EntityNumericId = EntityId & number;

/**
 * Casts a type to an EntityId.
 *
 * @private Should be used by more specific entity creators.
 */
function makeEntityId(id: Guid): EntityStringId;
function makeEntityId(id: number): EntityNumericId;
function makeEntityId(id: Guid | number): unknown {
  return id;
}

/**
 * Creates a unique, random EntityStringId.
 */
export const createEntityStringId = (): EntityStringId => {
  return makeEntityId(createGuid());
};

/**
 * Casts a string to an EntityStringId.
 */
export const makeEntityStringId = (id: Guid): EntityStringId => {
  return makeEntityId(id);
};

/**
 * Casts a number to an EntityNumericId.
 */
export const makeEntityNumericId = (id: number): EntityNumericId => {
  // Positive. Not abstract (NaN, Infinity or -Infinity). Integer.
  if (id <= Number.MAX_SAFE_INTEGER && id > 0 && id % 1 === 0) {
    return makeEntityId(id);
  }

  throw new Error(`"${id}" is not castable to an EntityNumericId.`);
};

export interface IEntity {
  // eslint-disable-next-line @typescript-eslint/ban-types
  attributes: {};
  // eslint-disable-next-line @typescript-eslint/ban-types
  associations: {};
  // eslint-disable-next-line @typescript-eslint/ban-types
  nestedAssociations?: {};
}

/**
 * Returns the IEntityType for something that looks like one of our associations.
 * @param T The association IEntity type
 * @returns IEntityType for that IEntity
 *
 * The T should be something like IEntity, IEntity[], IEntity | null, etc
 */
export type IEntityTypeFor<T> = T extends (infer R)[] ?
  (R extends IEntity ? IEntityType<R> : never) :
  T extends IEntity ? IEntityType<T> : never;

export interface IEntityType<T extends IEntity> {
  readonly storePrefix: string;
  readonly typeName: string;
  readonly version: number;
  readonly lockable: boolean;
  readonly attributes: {
    readonly [K in keyof T['attributes']]: {
      type: unknown;
      from: string;
      collection?: boolean;
      notNull?: boolean;
    };
  };
  readonly associations: {
    readonly [K in keyof T['associations']]: {
      type: IEntityTypeFor<T['associations'][K]>
      from: string;
      collection?: boolean;
      notNull?: boolean;
      shared?: boolean;
    };
  };
  readonly attributeNameMap: {
    [index: string]: keyof EntityFields<T> | undefined;
  };
  readonly extends?: IEntityType<any>;
  readonly kind: string,
  // `relatedTypes` and `externalAssociationTypes` are not part of the
  // `@engoo/view-model` package itself, but are added by the extended version
  // of `ViewModelClient` defined in `app/services/vmClient`.
  readonly relatedTypes: Array<IEntityType<any>>;
  readonly externalAssociationTypes: Array<IExternalAssociation>;
  new(args: EntityFields<T>): Entity<T>;
}

// EntityStringId is the default as it is considered the principled type by the backend.
// Places that use other brands or types of EntityId will have to be handled using special cases.
export interface IIdentifiableEntity<T extends EntityId = EntityStringId> extends IEntity {
  attributes: {
    id: T;
  };
}

// Use the value associated with the entity type.
type Ref<T extends IIdentifiableEntity<EntityId>> = T['attributes']['id'];

export type StringKeysOf<T> = Extract<keyof T, string>;

// This is for the fields that can be passed as props for generating an entity,
// but also the types of the fields on the entity.
export type EntityFields<T extends IEntity> = (
  EntityAttributes<T> &
  EntityAssociations<T> &
  EntityNestedAssociations<T>
);

export type EntityAttributes<T extends IEntity> = {
  [I in StringKeysOf<T['attributes']>]: NormalizedAttribute<T['attributes'][I]>;
};

export type EntityAssociations<T extends IEntity> = {
  [I in StringKeysOf<T['associations']>]: NormalizedAssociation<T['associations'][I]>;
};

// Treated the same as attributes, but given a different name because "real"
// attributes on the backend never reference entities.
//
// Note that nestedAssociations are only nested one level, and instead of
// referencing an IEntity interface, they should reference an Entity type
// directly.
export type EntityNestedAssociations<T extends IEntity> = {
  [I in StringKeysOf<T['nestedAssociations']>]: NormalizedAttribute<T['nestedAssociations'][I]>;
};

// Flatten the structure and transform attributes and associations to their desired types.
// An Entity is an Immutable.js Record with the given fields
export type Entity<T extends IEntity> = (
  Record<EntityFields<T>> &
  {
    schema: IEntityType<T>;
  }
);

/* This helper infers an IEntity type which is useful when you have a generic type argument that
 * unions over multiple IEntityTypes.
 *
 * const getField<T extends IEntity> = (
 *   type: IEntityType<T>,
 *   id: EntityId,
 *   field: keyof T extends any ? EntityFields<T>
 * ) => imaginaryGetEntityOfType(type, id)[field];
 *
 * const problem = (foo: A | B) => getField(foo.schema, foo.id, 'bar');
 *
 * You wind up with EntityField<A | B> instead of EntityFields<A> | EntityFields<B>. Using conditional types,
 * you can distribute over union types, so to fix this example, we distribute our helper to preserve the
 * union functionality.
 * getField<T extends IEntityType<any>> = (
 *   type: T,
 *   id: EntityId,
 *   field: T extends any ? keyof EntityFields<ExtractIEntity<T>> : never
 * ) => ...
 */
export type ExtractIEntity<T extends IEntityType<any>> = T extends IEntityType<infer E> ? E : never;

export const isEntityOfType = <T extends IEntity>(entity: Entity<any>, type: IEntityType<T>): entity is Entity<T> =>
  type.typeName === entity.schema.typeName;

// Normalize any attributes that extend IEntity. Their associations will be cast to EntityId by
// recursing into Normalize.
type NormalizedAttribute<T> =
  T extends undefined ? undefined :
  T extends null ? null :
  T extends (infer R)[] ?
    // IEntity are not allowed in attributes
    (R extends IEntity ? never : List<R>) :
    (T extends IEntity ? never : T);

// Convert associations to give back EntityId.
type NormalizedAssociation<T> =
  T extends undefined ? undefined :
  T extends null ? null :
  T extends (infer R)[] ?
    // Only IIdentifiableEntity are allowed in associations
    (R extends IIdentifiableEntity<EntityId> ? List<Ref<R>> : never) :
    (T extends IIdentifiableEntity<EntityId> ? Ref<T> : never);

export interface IExternalAssociation {
  typeName: string;
  storePrefix: string;
  associationType: IEntity;
}

// Experimental helpers for producing a partial version of an entity and its
// fields. This is useful to more accurately represent locally created entities,
// which may be incomplete (unlike entities parsed from backend responses).
export type PartialIEntity<T extends IEntity> = {
  attributes: Partial<T['attributes']>;
  associations: Partial<T['associations']>;
};
export type PartialEntity<T extends IEntity> = Entity<PartialIEntity<T>>;
export type PartialEntityFields<T extends IEntity> = Partial<EntityFields<T>>;
